import { Flex, Input, Select } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Icons, Typography } from "@reconvert/react-ui-component";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import ProductSelector from "../ConditionCreationSidebar/ProductSelector";
import ProductTagSelector from "../ConditionCreationSidebar/ProductTagSelector";
import ProductSkuSelector from "../ConditionCreationSidebar/ProductSkuSelector";
import { APP_PLATFORM } from "../../../../config";
import { Platform } from "../../../../models";
import ProductTextSelector from "../ConditionCreationSidebar/ProductTextSelector";
import { selectAppState } from "../../../../app/slice/appSlice";
import { getConditionsMetaData } from "../../../../utils/conditionsMetaData";
import {
  UNLEASH_MARKETING_CONSENT_CONDITION,
  UNLEASH_PRODUCT_PRICE_CONDITION,
  UNLEASH_PHYSICAL_PRODUCT_OR_REQUIRE_SHIPPING,
  UNLEASH_TOTAL_ITEM_COUNT_CONDITION,
  UNLEASH_SHIPPING_VALUE_CONDITION,
  UNLEASH_ORDER_NOTE_CONDITION,
  UNLEASH_ENABLE_PRODUCT_TYPE,
  UNLEASH_ENABLE_FULLFILLMENT_STATUS_CONDITION,
  UNLEASH_ENABLE_PAYMENT_STATUS_CONDITION,
  UNLEASH_ENABLE_PRODUCT_VENDOR_CONDITION,
  UNLEASH_ENABLE_SHIPPING_METHOD_CONDITION,
  UNLEASH_ENABLE_ORDER_TAGS_CONDITION,
  UNLEASH_PRODUCT_ITEM_COST_CONDITION,
  UNLEASH_PRODUCT_AVAILABLE_AT_SALES_CHANNEL_CONDITION,
  UNLEASH_ENABLE_PAYMENT_METHODS,
  UNLEASH_ENABLE_NUMBER_OF_ORDERS,
  UNLEASH_ENABLE_CUSTOMER_EMAIL,
  UNLEASH_ENABLE_TIME_SINCE_LAST_ORDER,
  UNLEASH_ENABLE_CUSTOMER_TAG,
  UNLEASH_ENABLE_CUSTOMER_BIRTHDAY,
  CONDITION_OPTIONS_GROUPED,
} from "../../../../utils/constant";
import { ConditionFact, PageType } from "@reconvert/reconvert-utils";
import { ConditonData } from "../../../../hooks/useAndOrGrouping";
import CartOrderSelector from "../ConditionCreationSidebar/CartOrderSelector";
import { useAppSelector } from "../../../../app/hooks";
import useIsActiveOnPage, { PagePurpose } from "../../../../hooks/useIsActiveOnPage";
import HelperText, { HelperTextProps } from "../ConditionCreationSidebar/HelperText";
import ItemSelector from "../ConditionCreationSidebar/ItemSelector";
import MultiLineTextSelector from "../ConditionCreationSidebar/MultiLineTextSelector";
import { useFlag } from "@unleash/proxy-client-react";
import DaySelector from "../ConditionCreationSidebar/DaySelector";
import DateSelector from "../ConditionCreationSidebar/DateSelector";
import { selectConditionsState } from "../../../../app/slice/conditionSlice";

const StyledSelect = styled(Select)`
  border: 1px solid #6a7381;
  transition: border-color 0.3s;
  height: 36px;

  &:hover {
    border: 2px solid #6a7381;
  }
`;

const AndOrConditionBox = ({
  conditionData,
  conditionsMetaData,
  handleDeleteCondition,
  updateConditionData,
  allCondition,
  conditionName,
  updateConditionName,
}: {
  conditionData: any;
  conditionsMetaData: any;
  handleDeleteCondition: () => void;
  updateConditionData: (condition: ConditonData) => void;
  allCondition: ConditonData[];
  conditionName: string;
  updateConditionName: (name: string) => void;
}) => {
  const { t } = useTranslation();
  
  const { activePage, storeInfo } = useAppSelector(selectAppState) as {
    activePage: any;
    storeInfo: any;
  };
  const isShippingValueConditionEnabled = useFlag(UNLEASH_SHIPPING_VALUE_CONDITION);
  const isPreviousOfferConditionApplicable = useIsActiveOnPage(PagePurpose.PREVIOUS_OFFER_CONDITION, activePage!);
  const isPrePurchasePages = useIsActiveOnPage(PagePurpose.PRE_PURCHASE_PAGES, activePage!);
  const isMarketingConsentCondition = useFlag(UNLEASH_MARKETING_CONSENT_CONDITION);
  const isProductPriceCondition = useFlag(UNLEASH_PRODUCT_PRICE_CONDITION);
  const isPhysicalProductOrRequireShipping = useFlag(UNLEASH_PHYSICAL_PRODUCT_OR_REQUIRE_SHIPPING);
  const isTotalItemCountCondition = useFlag(UNLEASH_TOTAL_ITEM_COUNT_CONDITION);
  const isOrderNoteConditionEnabled = useFlag(UNLEASH_ORDER_NOTE_CONDITION);
  const isProductTypeCondition = useFlag(UNLEASH_ENABLE_PRODUCT_TYPE);
  const isFullfillmentStatusConditionEnabled = useFlag(UNLEASH_ENABLE_FULLFILLMENT_STATUS_CONDITION);
  const isPaymentStatusConditionEnabled = useFlag(UNLEASH_ENABLE_PAYMENT_STATUS_CONDITION);
  const isProductVendorConditionEnabled = useFlag(UNLEASH_ENABLE_PRODUCT_VENDOR_CONDITION);
  const isShippingMethodConditionEnabled = useFlag(UNLEASH_ENABLE_SHIPPING_METHOD_CONDITION);
  const isOrderTagsConditionEnabled = useFlag(UNLEASH_ENABLE_ORDER_TAGS_CONDITION);
  const isItemCostCondition = useFlag(UNLEASH_PRODUCT_ITEM_COST_CONDITION);
  const isProductAvailableAtSalesChannelCondition = useFlag(UNLEASH_PRODUCT_AVAILABLE_AT_SALES_CHANNEL_CONDITION);
  const isPaymentMethodsEnabled = useFlag(UNLEASH_ENABLE_PAYMENT_METHODS);
  const isNumberOfOrdersConditionEnabled = useFlag(UNLEASH_ENABLE_NUMBER_OF_ORDERS);
  const isCustomerEmailConditionEnabled = useFlag(UNLEASH_ENABLE_CUSTOMER_EMAIL);
  const isTimeSinceLastOrderConditionEnabled = useFlag(UNLEASH_ENABLE_TIME_SINCE_LAST_ORDER);
  const isCustomerTagConditionEnabled = useFlag(UNLEASH_ENABLE_CUSTOMER_TAG);
  const isCustomerBirthdayConditionEnabled = useFlag(UNLEASH_ENABLE_CUSTOMER_BIRTHDAY);

  const { conditionErrors } = useAppSelector(selectConditionsState);

  const fieldErrors = useMemo(
    () => conditionErrors[conditionData.id] ?? {},
    [conditionErrors, conditionData.id],
  );

  const renderConditionValue = useCallback(
    (value: any) => {
      switch (value) {
        case ConditionFact.SPECIFIC_PRODUCT:
          return (
            <ProductSelector
              conditionFact={ConditionFact.SPECIFIC_PRODUCT}
              value={{ productIds: conditionData?.productIds || [], variantIds: conditionData?.variantIds || [] }}
              setValue={(data: { productIds: string[]; variantIds: string[]; itemsInfo?: string[] }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                  itemsInfo: data?.itemsInfo,
                })
              }
              fieldError={fieldErrors?.['productIds']}
            />
          );

        case ConditionFact.PRODUCT_COLLECTION:
          return (
            <ProductSelector
              conditionFact={ConditionFact.PRODUCT_COLLECTION}
              value={{ productCollectionIds: conditionData?.productCollectionIds, variantIds: [], productIds: [] }}
              setValue={(data: { productCollectionIds?: { id: string; name: string }[]; itemsInfo?: string[] }) =>
                updateConditionData({
                  ...conditionData,
                  productCollectionIds: data?.productCollectionIds,
                  id: conditionData.id,
                  itemsInfo: data?.itemsInfo,
                })
              }
              fieldError={fieldErrors?.['productCollectionIds']}
            />
          );

        case ConditionFact.PRODUCT_TAGS:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.PRODUCT_TAGS}
              label={t("Tags")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              isOptionCreatable
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.PRODUCT_PRICE:
          return (
            <CartOrderSelector
              conditionData={conditionData}
              updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              isOnlyPositiveValue={true}
              roundUpValue={2}
              fieldErrors={fieldErrors}
            />
          );

        case ConditionFact.CUSTOMER_LANGUAGE:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.CUSTOMER_LANGUAGE}
              label={t("Language")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.CUSTOMER_COUNTRY:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.CUSTOMER_COUNTRY}
              label={t("Customer country")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.PRODUCT_SALES_CHANNEL:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.PRODUCT_SALES_CHANNEL}
              label={t("Product sales channel")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL}
              label={t("Product sales channel")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              isValidationAdd
              validationMessage={t("No sales channels found. Connect at least one sales channel in Shopify to use this condition.")}
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.PRODUCT_TITLE:
          return (
            <ProductTextSelector
              titleText={t("Title")}
              value={conditionData?.value || ""}
              setTitleValue={(data: { value: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              fieldError={fieldErrors?.['value']}
            />
          );

        case ConditionFact.DISCOUNT_CODE:
          return (
            <ProductTextSelector
              titleText={t("Discount code")}
              value={conditionData?.value || ""}
              setTitleValue={(data: { value: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              fieldError={fieldErrors?.['value']}
            />
          );

        case ConditionFact.PRODUCT_VARIANT_NAME:
          return (
            <ProductTextSelector
              titleText={t("Title")}
              value={conditionData?.value || ""}
              setTitleValue={(data: { value: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              fieldError={fieldErrors?.['value']}
            />
          );

        case ConditionFact.PRODUCT_SKU:
          return (
            <ProductSkuSelector
              sku={conditionData?.value || ""}
              setSkuValue={(data: { value: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              fieldError={fieldErrors?.['value']}
            />
          );

        case ConditionFact.CART_OR_ORDER_VALUE:
          return (
            <CartOrderSelector
              conditionData={conditionData}
              updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              fieldErrors={fieldErrors}
            />
          );

        case ConditionFact.TOTAL_ITEM_COUNT:
          return (
            <ItemSelector
              conditionData={conditionData}
              updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              showTooltip={true}
              tooltipText={t(
                "The total item count includes all products in the cart, using their quantity values. Example: 2 × Product A + 3 × Product B = total item count of 5"
              )}
              showCurrency={false}
              fieldErrors={fieldErrors}
            />
          );

        case ConditionFact.CUSTOMER_SEGMENTS:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.CUSTOMER_SEGMENTS}
              label={t("Customer Segments")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.PREV_PAGE_OFFER:
          if (conditionData.thirdOperatorOption === "ONEOFSELECTED") {
            return (
              <ProductTagSelector
                conditionData={conditionData}
                conditionFact={ConditionFact.PREV_PAGE_OFFER}
                label={t("Condition list")}
                itemsValue={conditionData?.items || []}
                setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                  updateConditionData({
                    ...conditionData,
                    items: data.items,
                    itemsInfo: data.info,
                    id: conditionData.id,
                  });
                }}
                fieldError={fieldErrors?.['items']}
              />
            );
          } else {
            return <></>;
          }
        
        case ConditionFact.SHIPPING_VALUE:
          return (
            <ItemSelector
              showTooltip
              tooltipText={t("This value reflects the customer’s final shipping cost after discounts.")}
              allowDecimals={true}
              label={t("Shipping value")}
              conditionData={conditionData}
              updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              fieldErrors={fieldErrors}
            />
          );

        case ConditionFact.ORDER_NOTE:
        return (
          <MultiLineTextSelector
           showToolTip
           toolTipText={t("Text comparison is case-insensitive. Leading and trailing spaces are ignored.")}
            titleText={t("Order note")}
            value={conditionData?.value || ""}
            setTitleValue={(data: { value: string }) =>
              updateConditionData({
                ...conditionData,
                ...data,
                id: conditionData.id,
              })
            }
            fieldError={fieldErrors?.['value']}
          />
        );

        case ConditionFact.PRODUCT_TYPE:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.PRODUCT_TYPE}
              label={t("Product type")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              isValidationAdd
              validationMessage={t("No product types found. Set product types on your products in Shopify to use this condition")}
              fieldError={fieldErrors?.['items']}
            />
          );
        case ConditionFact.FULFILLMENT_STATUS:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.FULFILLMENT_STATUS}
              label={t("Fulfillment status")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              fieldError={fieldErrors?.['items']}
            />
          );
        case ConditionFact.PAYMENT_STATUS:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.PAYMENT_STATUS}
              label={t("Payment status")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              fieldError={fieldErrors?.['items']}
            />
          );
        case ConditionFact.PRODUCT_VENDOR:
            return (
              <ProductTagSelector
                conditionFact={ConditionFact.PRODUCT_VENDOR}
                label={t("Product vendor")}
                itemsValue={conditionData?.items || []}
                setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                  updateConditionData({
                    ...conditionData,
                    items: data.items,
                    itemsInfo: data.info,
                    id: conditionData.id,
                  });
                }}
                isValidationAdd
                validationMessage={t("No vendors found. Set vendor names on your products in Shopify to use this condition.")}
                fieldError={fieldErrors?.['items']}
              />
            );
        case ConditionFact.SHIPPING_METHOD:
            return (
              <ProductTagSelector
                conditionFact={ConditionFact.SHIPPING_METHOD}
                label={t("Shipping methods")}
                itemsValue={conditionData?.items || []}
                setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                  updateConditionData({
                    ...conditionData,
                    items: data.items,
                    itemsInfo: data.info,
                    id: conditionData.id,
                  });
                }}
                isValidationAdd
                validationMessage={t("Please select at least one shipping method")}
                fieldError={fieldErrors?.['items']}
              />
            );
        case ConditionFact.ORDER_TAGS:
            return (
              <ProductTagSelector
                conditionFact={ConditionFact.ORDER_TAGS}
                label={t("Order tags")}
                itemsValue={conditionData?.items || []}
                setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                  updateConditionData({
                    ...conditionData,
                    items: data.items,
                    itemsInfo: data.info,
                    id: conditionData.id,
                  });
                }}
                isOptionCreatable
                showToolTip
                toolTipText={t("Tags are case-insensitive and compared exactly as they appear on the order.")}
                fieldError={fieldErrors?.['items']}
              />
            )
        case ConditionFact.PRODUCT_ITEM_COST:
          return (
            <ItemSelector
              conditionData={conditionData}
              updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              showTooltip={true}
              tooltipText={t(
                "Compares each product’s cost of goods (COGS) as stored in Shopify. This is not the selling price."
              )}
              showCurrency
              allowDecimals
              fieldErrors={fieldErrors}
            />
          );

        case ConditionFact.PAYMENT_METHOD:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.PAYMENT_METHOD}
              label={t("Payment method")}
              itemsValue={conditionData?.items || []}
              isValidationAdd
              validationMessage={t("No payment method found. Enable at least one payment method in Shopify to use this condition.")}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.CUSTOMER_NUMBER_OF_ORDERS:
          return (
            <ItemSelector
              conditionData={conditionData}
              updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              showCurrency={false}
              fieldErrors={fieldErrors}
            />
          );

        case ConditionFact.CUSTOMER_EMAIL:
          return (
            <ProductTextSelector
              titleText={t("Customer email")}
              value={conditionData?.value || ""}
              setTitleValue={(data: { value: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              placeholder={t("Enter email or part of email")}
              isWhiteSpaceAllowed={false}
              warningMessage={t("Email addresses can’t contain spaces. This may never match.")}
              fieldError={fieldErrors?.['value']}
            />
          )

        case ConditionFact.CUSTOMER_TIME_SINCE_LAST_ORDER:
          return (
            <DaySelector
              conditionData={conditionData}
              updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                updateConditionData({
                  ...conditionData,
                  ...data,
                  id: conditionData.id,
                })
              }
              validationMessage={t("Minimum days must be less than or equal to maximum days.")}
              fieldErrors={fieldErrors}
            />
          );

        case ConditionFact.CUSTOMER_TAGS:
          return (
            <ProductTagSelector
              conditionFact={ConditionFact.CUSTOMER_TAGS}
              label={t("Customer tag")}
              itemsValue={conditionData?.items || []}
              setItemsValue={(data: { items: string | string[]; info: string | string[] }) => {
                updateConditionData({
                  ...conditionData,
                  items: data.items,
                  itemsInfo: data.info,
                  id: conditionData.id,
                });
              }}
              isOptionCreatable
              placeholder={t("Select or type customer tags")}
              isCustomerTag={true}
              fieldError={fieldErrors?.['items']}
            />
          );

        case ConditionFact.CUSTOMER_BIRTHDAY:
          
          switch (conditionData?.condition) {
            case "lessThan":
            case "elapsed":
              return (
                  <DaySelector
                    conditionData={conditionData}
                    updateConditionData={(data: { value?: string; fromValue?: string; toValue?: string }) =>
                      updateConditionData({
                        ...conditionData,
                        ...data,
                        id: conditionData.id,
                      })
                    }
                    validationMessage={t("Minimum days must be less than or equal to maximum days.")}
                    fieldErrors={fieldErrors}
                  />  
                );
                
            default:
              return (
                <DateSelector
                  conditionData={conditionData}
                  updateConditionData={(data: { value?: number; fromValue?: number; toValue?: number }) =>
                    updateConditionData({
                      ...conditionData,
                      ...data,
                      id: conditionData.id,
                    })
                  }
                  fieldErrors={fieldErrors}
                />
              );
           }

        default:
          break;
      }
    },
    [conditionData, updateConditionData, fieldErrors],
  );

  const getHelperTextData = useCallback((conditionFact: ConditionFact) => {
    switch (conditionFact) {
      case ConditionFact.PHYSICAL_PRODUCT_OR_REQUIRE_SHIPPING:
        return {
          text: t("Physical items require shipping. digital items (like downloads) do not."),
          showTooltip: false,
        }
      case ConditionFact.CUSTOMER_NUMBER_OF_ORDERS:
        return {
          text: t("Order count is based on the lifetime orders Shopify has recorded for this customer before this order."),
          showTooltip: false,
        }
      default:
        return null;
    }
  }, []);

  const getConditionOptions = useCallback(
    (t: TFunction<"translation", undefined>) => {
      const options = [
        { value: conditionsMetaData.SPECIFIC_PRODUCT.value, label: t("Specific product") },
        // { value: conditionsMetaData.PRODUCT_COLLECTION.value, label: t("Product collection") },
        // { value: conditionsMetaData.PRODUCT_VARIANT_NAME.value, label: t("Variant name") },
        // { value: conditionsMetaData.DISCOUNT_CODE.value, label: t("Discount code") },
        // { value: conditionsMetaData.CUSTOMER_LANGUAGE.value, label: t("Language") },
        // { value: conditionsMetaData.CUSTOMER_COUNTRY.value, label: t("Customer country") },
      ];

      if (APP_PLATFORM === Platform.Shopify) {
        options.push({ value: conditionsMetaData.PRODUCT_TITLE.value, label: t("Product title") });
        options.push({ value: conditionsMetaData.PRODUCT_COLLECTION.value, label: t("Product collection") });

        options.push({ value: conditionsMetaData.PRODUCT_SKU.value, label: t("Product SKU") });
        options.push({ value: conditionsMetaData.CART_OR_ORDER_VALUE.value, label: t("Cart/Order value") });
        options.push({ value: conditionsMetaData.PRODUCT_TAGS.value, label: t("Product tags") });
        options.push({ value: conditionsMetaData.PRODUCT_VARIANT_NAME.value, label: t("Variant name") });
        options.push({ value: conditionsMetaData.DISCOUNT_CODE.value, label: t("Discount code") });
        options.push({ value: conditionsMetaData.CUSTOMER_LANGUAGE.value, label: t("Language") });
        options.push({ value: conditionsMetaData.CUSTOMER_COUNTRY.value, label: t("Customer country") });
        
        if (!isPrePurchasePages) {  // Hide the Customer segments condition for pre purchase pages
          options.push({ value: conditionsMetaData.CUSTOMER_SEGMENTS.value, label: t("Customer Segments") });
        }
        // options.push({ value: conditionsMetaData.PRODUCT_SALES_CHANNEL.value, label: t("Product sales channel") });

        if (isMarketingConsentCondition && isPreviousOfferConditionApplicable) { // Show the Marketing consent condition only for POP, TYP and OSP page
          options.push({ value: conditionsMetaData.MARKETING_CONSENT.value, label: t("Marketing consent") });
        }

        if (isProductPriceCondition) {
          options.push({ value: conditionsMetaData.PRODUCT_PRICE.value, label: t("Product price") });
        }

        if (isPhysicalProductOrRequireShipping) {
          options.push({ value: conditionsMetaData.PHYSICAL_PRODUCT_OR_REQUIRE_SHIPPING.value, label: t("Physical product or require shipping") });
        }

        if (isTotalItemCountCondition) {
          options.push({ value: conditionsMetaData.TOTAL_ITEM_COUNT.value, label: t("Total item count") })
        }

        if (isProductTypeCondition) {
          options.push({ value: conditionsMetaData.PRODUCT_TYPE.value, label: t("Product type") });
        }

        if (isProductVendorConditionEnabled) {
          options.push({ value: conditionsMetaData.PRODUCT_VENDOR.value, label: t("Product vendor") });
        }
        
        if (isItemCostCondition) {
          options.push({ value: conditionsMetaData.PRODUCT_ITEM_COST.value, label: t("Item cost") });
        }

        if (isProductAvailableAtSalesChannelCondition) {
          options.push({ value: conditionsMetaData.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL.value, label: t("Product is available at sales channel") });
        }

        if (isPaymentMethodsEnabled && isPreviousOfferConditionApplicable) { // Show the Payment method condition only for POP, TYP and OSP page
          options.push({ value: conditionsMetaData.PAYMENT_METHOD.value, label: t("Payment method") });
        }
        
        if (isNumberOfOrdersConditionEnabled && isPreviousOfferConditionApplicable) {
          options.push({ value: conditionsMetaData.CUSTOMER_NUMBER_OF_ORDERS.value, label: t("Number of orders") });
        }

        if (isCustomerEmailConditionEnabled && isPreviousOfferConditionApplicable) {
          options.push({ value: conditionsMetaData.CUSTOMER_EMAIL.value, label: t("Customer email") });
        }
        
        if (isTimeSinceLastOrderConditionEnabled && isPreviousOfferConditionApplicable) {
          options.push({ value: conditionsMetaData.CUSTOMER_TIME_SINCE_LAST_ORDER.value, label: t("Time since last order") });
        }

        if (isCustomerTagConditionEnabled && isPreviousOfferConditionApplicable) {
          options.push({ value: conditionsMetaData.CUSTOMER_TAGS.value, label: t("Customer tag") });
        }

        if (isCustomerBirthdayConditionEnabled && isPreviousOfferConditionApplicable) {
          options.push({ value: conditionsMetaData.CUSTOMER_BIRTHDAY.value, label: t("Customer birthday") });
        }

        //Previous Offer
        if (isPreviousOfferConditionApplicable) {
          if (activePage === PageType.POST_PURCHASE_PAGE1) {
            if (storeInfo?.isShopifyPlus) {
              options.push({ value: conditionsMetaData.PREV_PAGE_OFFER.value, label: t("Previous offer") });
            }
          } else {
            options.push({ value: conditionsMetaData.PREV_PAGE_OFFER.value, label: t("Previous offer") });
          }
        }
      } else {
        options.push({ value: conditionsMetaData.PRODUCT_TITLE.value, label: t("Product title") });
        options.push({ value: conditionsMetaData.PRODUCT_COLLECTION.value, label: t("Product category") });

        options.push({ value: conditionsMetaData.PRODUCT_SKU.value, label: t("Product SKU") });
        options.push({ value: conditionsMetaData.CART_OR_ORDER_VALUE.value, label: t("Cart/Order value") });
        options.push({ value: conditionsMetaData.PRODUCT_VARIANT_NAME.value, label: t("Variant name") });
        options.push({ value: conditionsMetaData.DISCOUNT_CODE.value, label: t("Discount code") });
      }
      
      if(isOrderTagsConditionEnabled && (activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2 || activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE)) {
        options.push({ value: conditionsMetaData.ORDER_TAGS.value, label: t("Order tags") });
      }

      if (isShippingValueConditionEnabled && (activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2 || activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE)) {
        options.push({ value: conditionsMetaData[ConditionFact.SHIPPING_VALUE].value, label: t("Shipping value") });
      }

      if(isOrderNoteConditionEnabled && (activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2 || activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE)) {
        options.push({ value: conditionsMetaData.ORDER_NOTE.value, label: t("Order note") });
      }
      
      if(isFullfillmentStatusConditionEnabled && (activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2  || activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE)) {
        options.push({ value: conditionsMetaData.FULFILLMENT_STATUS.value, label: t("Fulfillment status") });
      }
      
      if (isPaymentStatusConditionEnabled && (activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2 || activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE)) {
        options.push({ value: conditionsMetaData.PAYMENT_STATUS.value, label: t("Payment status") });
      }
      
      if(isShippingMethodConditionEnabled && (activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2 || activePage === PageType.THANK_YOU_PAGE || activePage === PageType.ORDER_STATUS_PAGE)) {
        options.push({ value: conditionsMetaData.SHIPPING_METHOD.value, label: t("Shipping methods") });
      }

      return options;
    },
    [activePage, storeInfo?.isShopifyPlus],
  );

  /**
   * Get the condition options grouped by the condition type
   */
  const getConditionOptionsGrouped = useCallback((t: TFunction<"translation", undefined>) => {
    const options = getConditionOptions(t);

    return CONDITION_OPTIONS_GROUPED.map(({ groupLabel, facts }) => ({
      groupLabel: t(groupLabel),
      options: facts
        .map((fact) => options.find((option) => option.value === fact))
        .filter(Boolean),
    }));
  }, [conditionsMetaData, getConditionOptions]);

  const updateOperatorValue = (e: any) => {
    const operatorValue = getConditionsMetaData(activePage!, storeInfo?.isShopifyPlus!, e.target.value, t);
    const conditionKey = conditionData.fact as ConditionFact;

    const conditionalOperator = [ConditionFact.SPECIFIC_PRODUCT, ConditionFact.PRODUCT_TITLE, ConditionFact.PRODUCT_COLLECTION, ConditionFact.PRODUCT_SKU, ConditionFact.PRODUCT_TAGS, ConditionFact.PRODUCT_VARIANT_NAME, ConditionFact.PRODUCT_PRICE, ConditionFact.PRODUCT_TYPE, ConditionFact.PRODUCT_VENDOR, ConditionFact.PRODUCT_ITEM_COST, ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL];
    const conditionalOperatorValue = ["entireProduct"]
    const payload = {
      ...conditionData,
      id: conditionData.id,
      condition: e.target.value,
      operator: operatorValue[conditionKey]?.operatorOptions[0]?.value,
    };
    
    if(conditionalOperator.includes(conditionKey)) {
      if(conditionalOperatorValue.includes(payload.condition)) {
        payload.path = "$.page";
      } else {
        payload.path = "$.cart";
      }
    } else {
      if(payload.path) delete payload.path;
    }

    if(conditionKey === ConditionFact.CUSTOMER_BIRTHDAY) {
      payload.value = "";
    }

    updateConditionData(payload);
  };

  return (
    <>
      <Flex pt={4} flexDirection={"column"}>
        <Flex gap={"8px"} flexDirection={"column"}>
          <Flex gap={1} flexDirection={"column"}>
            <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
              {t("Condition")}
            </Typography>
            <StyledSelect
              value={conditionData.fact}
              onChange={(e: any) => {
                const conditionKey = e.target.value as ConditionFact;

                const operatorValue = getConditionsMetaData(
                  activePage!,
                  storeInfo?.isShopifyPlus,
                  conditionsMetaData[e.target.value]?.options[0]?.value,
                  t
                );

                const conditionalOperator = [ConditionFact.SPECIFIC_PRODUCT, ConditionFact.PRODUCT_TITLE, ConditionFact.PRODUCT_COLLECTION, ConditionFact.PRODUCT_SKU, ConditionFact.PRODUCT_TAGS, ConditionFact.PRODUCT_VARIANT_NAME, ConditionFact.PRODUCT_PRICE, ConditionFact.PRODUCT_TYPE, ConditionFact.PRODUCT_VENDOR, ConditionFact.PRODUCT_ITEM_COST, ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL];
                const conditionalOperatorValue = ["entireProduct"]

                const payload = {
                  ...conditionData,
                  id: conditionData.id,
                  fact: e.target.value,
                  condition: operatorValue[conditionKey]?.options[0]?.value || "",
                  operator: operatorValue[conditionKey]?.operatorOptions[0]?.value || "",
                  value: "",
                  items: [],
                  productIds: [],
                  productCollectionIds: [],
                  variantIds: [],
                };

                if(conditionalOperator.includes(conditionKey)) {
                  if(conditionalOperatorValue.includes(payload.condition)) {
                    payload.path = "$.page";
                  } else {
                    payload.path = "$.cart";
                  }
                } else {
                  if(payload.path) delete payload.path;
                }

                updateConditionData(payload);
              }}
            >
              {getConditionOptionsGrouped(t).map(item => {
                return (
                  item.options.length  ? (
                    <optgroup key={item.groupLabel} label={item.groupLabel}>
                      {item.options.map((option: any) => {
                        return <option key={option.value} value={option.value}>{option.label}</option>;
                      })}
                    </optgroup>
                  ) : (
                    <></>
                  )
                );
              })}
            </StyledSelect>
          </Flex>
        </Flex>

        {conditionData.fact && (
          <>
            {conditionsMetaData[conditionData?.fact]?.options.length > 0 && (
              <Flex mt="2" gap={1} flexDirection={"column"}>
                <Typography color={"#6A7381"} bg={"var(--Natural-100, #F9FAFC)"} variant="small" width={"max-content"}>
                  {t("Operator 1")}
                </Typography>
                <StyledSelect
                  value={conditionData.condition}
                  onChange={(e: any) => {
                    updateOperatorValue(e);
                  }}
                  isInvalid={!!fieldErrors?.condition}
                  borderColor={fieldErrors?.condition ? "red.500" : undefined}
                >
                  {conditionsMetaData[conditionData.fact as keyof typeof conditionsMetaData].options?.map((o: any) => {
                    return <option value={o.value}>{t(o.label)}</option>;
                  })}
                </StyledSelect>
                {fieldErrors?.condition && (
                  <Typography variant="small" color="red.500" mt={0.5}>
                    {fieldErrors.condition}
                  </Typography>
                )}
              </Flex>
            )}

          {conditionsMetaData[conditionData?.fact]?.operatorOptions?.length > 0 && (
            <Flex mt="8px" gap={1} flexDirection={"column"}>
              <Typography color={"#6A7381"} bg={"var(--Natural-100, #F9FAFC)"} variant="small" width={"max-content"}>
                {conditionsMetaData[conditionData?.fact]?.options.length && conditionsMetaData[conditionData?.fact]?.operatorOptions?.length ? t("Operator 2") : t("Operator 1")}
              </Typography>
              <StyledSelect
                value={conditionData?.operator}
                onChange={(e: any) => {
                  updateConditionData({
                    ...conditionData,
                    operator: e.target.value,
                    id: conditionData.id,
                  });
                }}
                isInvalid={!!fieldErrors?.operator}
                borderColor={fieldErrors?.operator ? "red.500" : undefined}
              >
                {conditionsMetaData[conditionData.fact as keyof typeof conditionsMetaData]?.operatorOptions?.map(
                  (o: any) => {
                    return <option value={o.value}>{t(o.label)}</option>;
                  },
                )}
              </StyledSelect>
              {fieldErrors?.operator && (
                <Typography variant="small" color="red.500" mt={0.5}>
                  {fieldErrors.operator}
                </Typography>
              )}
            </Flex>
          )}

            {conditionData.fact && (
              <>
                {conditionsMetaData[conditionData?.fact]?.thirdOperatorOptions ? (
                  <Flex mt="2" gap={1} flexDirection={"column"}>
                    <Typography
                      color={"#6A7381"}
                      bg={"var(--Natural-100, #F9FAFC)"}
                      variant="small"
                      width={"max-content"}
                    >
                      {t("Operator 3")}
                    </Typography>
                    <StyledSelect
                      value={conditionData?.thirdOperatorOption ?? ""}
                      onChange={(e: any) => {
                        updateConditionData({
                          ...conditionData,
                          thirdOperatorOption: e.target.value,
                          id: conditionData.id,
                        });
                      }}
                    >
                      {conditionsMetaData[conditionData.fact as keyof typeof conditionsMetaData]
                        .thirdOperatorOptions ? (
                        conditionsMetaData[
                          conditionData.fact as keyof typeof conditionsMetaData
                        ]?.thirdOperatorOptions?.map((o: any) => {
                          return <option value={o.value}>{t(o.label)}</option>;
                        })
                      ) : (
                        <></>
                      )}
                    </StyledSelect>
                  </Flex>
                ) : (
                  <></>
                )}
              </>
            )}

            {renderConditionValue(conditionData.fact as keyof typeof conditionsMetaData)}

            {getHelperTextData(conditionData.fact as ConditionFact) && (
              <HelperText {...getHelperTextData(conditionData.fact as ConditionFact) as HelperTextProps} />
            )}

            {allCondition?.length > 1 && (
              <Flex mt={4} gap="10px" justifyContent={"end"} alignItems={"center"}>
                <Icons.TrashSimple height={"16px"} width={"16px"} onClick={handleDeleteCondition} cursor={"pointer"} />
              </Flex>
            )}
          </>
        )}
      </Flex>
    </>
  );
};


export default React.memo(AndOrConditionBox);
